import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Sparkles, Compass, MoonStar, Sun, Users } from 'lucide-react';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';

const NotFound = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 overflow-x-hidden font-sans selection:bg-purple-900 selection:text-white relative flex flex-col">
            <BackgroundLayer />
            <Header />

            <main className="relative z-10 flex-grow flex items-center justify-center pt-20 pb-24 px-6">
                {/* Decorative background glows */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 blur-[120px] rounded-full pointer-events-none" />
                <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-pink-600/5 blur-[100px] rounded-full pointer-events-none" />

                <div className="container mx-auto max-w-5xl relative">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* Left Side: Illustration / Large Text */}
                        <div className="text-center lg:text-left order-1 lg:order-1">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-8 backdrop-blur-sm">
                                <Sparkles size={16} className="text-purple-400 rotate-12" />
                                <span className="text-xs font-bold text-purple-300 uppercase tracking-widest leading-none">Cosmic Error 404</span>
                            </div>

                            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight font-serif tracking-tight">
                                <span className="block text-white">Lost in the</span>
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">
                                    Celestial Void
                                </span>
                            </h1>

                            <p className="text-purple-200/70 text-lg md:text-xl leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0 font-sans">
                                The stars are misaligned. This path has drifted beyond the realms of existing constellations. Let us guide you back to clarity.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate(-1)}
                                    className="group px-8 py-4 bg-white/5 border border-white/10 rounded-2xl text-white font-bold text-sm uppercase tracking-wider hover:bg-white/10 transition-all flex items-center justify-center gap-2 backdrop-blur-md"
                                >
                                    <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                                    <span>Reverse Orbit</span>
                                </button>

                                <Link
                                    to="/"
                                    className="group px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl text-white font-bold text-sm uppercase tracking-wider shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <Home size={18} className="relative z-10" />
                                    <span className="relative z-10">Return Home</span>
                                </Link>
                            </div>
                        </div>

                        {/* Right Side: Large 404 & Quick Links */}
                        <div className="relative order-2 lg:order-2">
                            {/* 404 Text Background */}
                            <div className="absolute inset-0 flex items-center justify-center select-none pointer-events-none">
                                <h2 className="text-[180px] md:text-[280px] font-black text-white/[0.03] leading-none font-serif">404</h2>
                            </div>

                            <div className="relative z-10 grid grid-cols-2 gap-4">
                                {[
                                    { name: 'Kundli', icon: Sun, path: '/free-kundli', color: 'from-orange-500 to-red-500' },
                                    { name: 'Horoscope', icon: MoonStar, path: '/horoscope', color: 'from-purple-500 to-pink-500' },
                                    { name: 'Panchang', icon: Compass, path: '/panchang', color: 'from-teal-500 to-emerald-500' },
                                    { name: 'Community', icon: Users, path: '/discussion', color: 'from-blue-500 to-indigo-500' }
                                ].map((item, i) => (
                                    <Link
                                        key={i}
                                        to={item.path}
                                        className="p-6 bg-cosmic-900/40 backdrop-blur-xl border border-white/10 rounded-3xl hover:border-purple-500/50 hover:bg-purple-500/5 transition-all group overflow-hidden relative"
                                    >
                                        <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${item.color} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity blur-2xl rounded-full -mr-12 -mt-12`} />

                                        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg shadow-black/20 group-hover:scale-110 transition-transform`}>
                                            <item.icon size={24} className="text-white" />
                                        </div>

                                        <h3 className="font-serif text-white font-bold text-lg tracking-tight mb-1">{item.name}</h3>
                                        <span className="text-purple-300/40 text-[10px] uppercase font-bold tracking-widest group-hover:text-purple-300/60 transition-colors">Launch Service</span>
                                    </Link>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default NotFound;
